/**
 * Resources service — reads study materials (PDFs) straight from Firebase
 * Storage so the catalogue stays 100% driven by what's uploaded, with no
 * Firestore bookkeeping to maintain.
 *
 * Expected layout in the bucket:
 *
 *   resources/{subjectFolder}/{unitFolder}/file.pdf   → grouped under "Unit …"
 *   resources/{subjectFolder}/file.pdf                → grouped under "General"
 *
 * Admins simply drop PDFs into the right folder (via the Firebase console);
 * they appear in the app automatically. Reads are gated to signed-in users by
 * the Storage rules (see storage.rules).
 */
import {
  ref,
  listAll,
  getDownloadURL,
  getMetadata,
} from 'firebase/storage';

import { storage } from './config';

const ROOT = 'resources';
const GENERAL_UNIT = '_general';

/** "unit-1" / "unit_2_intro" → "Unit 1" / "Unit 2 Intro". */
function prettify(name) {
  const words = name.replace(/[-_]+/g, ' ').trim();
  if (!words) return 'Untitled';
  return words.replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Drop the extension and tidy separators for a human-readable file title. */
function fileTitle(name) {
  return name.replace(/\.[^.]+$/, '').replace(/[-_]+/g, ' ').trim() || name;
}

/** Natural sort by title so "Unit 2" comes before "Unit 10". */
function byTitle(a, b) {
  return a.title.localeCompare(b.title, undefined, { numeric: true, sensitivity: 'base' });
}

function byName(a, b) {
  return a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' });
}

/** Resolve a Storage item into the metadata the UI needs (url, size, type…). */
async function describe(item) {
  const [url, meta] = await Promise.all([
    getDownloadURL(item),
    getMetadata(item).catch(() => ({})),
  ]);
  return {
    name: item.name,
    title: meta.customMetadata?.title || fileTitle(item.name),
    url,
    path: item.fullPath,
    size: meta.size || 0,
    contentType: meta.contentType || '',
    updatedAt: meta.updated ? new Date(meta.updated).getTime() : null,
  };
}

/** List the files directly inside a folder ref and wrap them as a "unit". */
async function unitFrom(folderRef, id, name) {
  const { items } = await listAll(folderRef);
  const files = (await Promise.all(items.map(describe)))
    // Only surface PDFs — ignore stray cover images / thumbnails.
    .filter((f) => f.contentType.includes('pdf') || /\.pdf$/i.test(f.name))
    .sort(byTitle);
  return { id, name, files };
}

export const resourcesService = {
  /**
   * Load every unit + PDF for one subject folder. Resolves to an array of
   * `{ id, name, files: [{ name, title, url, size, contentType, updatedAt, path }] }`,
   * with loose top-level files collected under a leading "General" unit. Empty
   * units are omitted.
   */
  async listSubject(folder) {
    const subjectRef = ref(storage, `${ROOT}/${folder}`);
    const { items, prefixes } = await listAll(subjectRef);

    const units = [];

    // Files sitting directly under the subject → a "General" unit.
    if (items.length) {
      const general = await unitFrom(subjectRef, GENERAL_UNIT, 'General');
      if (general.files.length) units.push(general);
    }

    // Each sub-folder → its own unit.
    const subUnits = (
      await Promise.all(prefixes.map((p) => unitFrom(p, p.name, prettify(p.name))))
    )
      .filter((u) => u.files.length)
      .sort(byName);

    units.push(...subUnits);
    return units;
  },
};

export default resourcesService;
